import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Button,
  Typography,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";
import {
  listModels,
  setCurrentModel as setCurrentModelService,
} from "./assistant-service";

const ModelView = () => {
  const { apiCall } = useApiCall();
  const { errorToast, successToast } = useToast();

  const [models, setModels] = useState([]);
  const [currentModel, setCurrentModel] = useState("");
  const [loading, setLoading] = useState(false);
  const [settingModel, setSettingModel] = useState(false);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await listModels(apiCall);

      if (response && response.models) {
        setModels(response.models);
        setCurrentModel(response.current || "");
      } else {
        setModels([]);
        setCurrentModel("");
      }
      setLoading(false);
    } catch (error) {
      errorToast("Failed to fetch models");
      setLoading(false);
    }
  };

  const handleModelSelect = async model => {
    if (model === currentModel) {
      return; // Already selected
    }

    try {
      setSettingModel(true);
      const response = await setCurrentModelService(apiCall, model);

      if (response) {
        setCurrentModel(model);
        successToast(`Model changed to ${model}`);
      }
      setSettingModel(false);
    } catch (error) {
      errorToast("Failed to set model");
      setSettingModel(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Grid container direction="column" spacing={3}>
        {/* Header */}
        <Grid item>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" component="h1">
              Model Management
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchModels}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Grid>

        {/* Current Model Info */}
        {currentModel && (
          <Grid item>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body1">
                <strong>Current Model:</strong> {currentModel}
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Models List */}
        <Grid item>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <LoadingSpinner />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {models.length === 0 ? (
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    align="center"
                  >
                    No models available
                  </Typography>
                </Grid>
              ) : (
                models.map((model, index) => {
                  const isCurrentModel = model === currentModel;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          border: isCurrentModel
                            ? "2px solid #1976d2"
                            : "1px solid #e0e0e0",
                          cursor: isCurrentModel ? "default" : "pointer",
                          "&:hover": {
                            boxShadow: isCurrentModel ? 3 : 2,
                          },
                        }}
                        onClick={() =>
                          !isCurrentModel &&
                          !settingModel &&
                          handleModelSelect(model)
                        }
                      >
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            {isCurrentModel ? (
                              <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
                            ) : (
                              <RadioButtonUncheckedIcon
                                color="action"
                                sx={{ mr: 1 }}
                              />
                            )}
                            <Typography variant="h6" component="h2">
                              {model}
                            </Typography>
                          </Box>
                          {isCurrentModel && (
                            <Chip
                              label="Current"
                              color="primary"
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            variant={isCurrentModel ? "outlined" : "contained"}
                            disabled={isCurrentModel || settingModel}
                            onClick={e => {
                              e.stopPropagation();
                              handleModelSelect(model);
                            }}
                            fullWidth
                          >
                            {isCurrentModel
                              ? "Active"
                              : settingModel
                              ? "Setting..."
                              : "Select"}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ModelView;
